import React from 'react';
import { AlertCircle, User, Clock } from 'lucide-react';

/**
 * Warning banner shown when another user is accessing a job card
 */
export function LockWarningBanner({ lockedByName, lockedAt, onDismiss }) {
  const getTimeAgo = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${Math.floor(diffHours / 24)}d ago`;
    } catch {
      return '';
    }
  };

  return (
    <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 mb-4 animate-pulse">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-semibold text-amber-800 text-lg">
            Job Sheet Currently In Use
          </h4>
          <div className="flex items-center gap-4 mt-2 text-amber-700">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              <span className="font-medium">{lockedByName || 'Another user'}</span>
            </span>
            {lockedAt && (
              <span className="flex items-center gap-1 text-sm">
                <Clock className="w-4 h-4" />
                <span>Since {getTimeAgo(lockedAt)}</span>
              </span>
            )}
          </div>
          <p className="text-sm text-amber-600 mt-2">
            You can view this job sheet, but changes may conflict with theirs. 
            Consider checking back later or coordinating with them.
          </p>
        </div>
        {onDismiss && (
          <button 
            onClick={onDismiss}
            className="text-amber-600 hover:text-amber-800 text-sm underline"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Small indicator badge for showing lock status on job cards in lists
 */
export function LockIndicator({ lockedByName, isCurrentUser }) {
  if (isCurrentUser) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
        <User className="w-3 h-3" />
        You
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full animate-pulse">
      <User className="w-3 h-3" />
      {lockedByName || 'In use'}
    </span>
  );
}

/**
 * Banner shown to the current user that they have the lock
 */
export function ActiveLockBanner({ screen }) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 mb-4 flex items-center gap-2">
      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
      <span className="text-sm text-blue-700">
        You are currently editing this job sheet
      </span>
      <span className="text-xs text-blue-500">
        ({screen})
      </span>
    </div>
  );
}

export default LockWarningBanner;
